import React, { useState, Fragment, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useResetPassword, useValidateResetLink } from "Api/Password";
import ClooserLogo from "../../../src/assets/utils/images/logo_Clooser_blue_orange.png";
import "../../../src/assets/pages/_loginBoxed.scss";

const ChangedPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchParams] = useSearchParams();
  const resetCode = searchParams.get("reset");
  const time = searchParams.get("time");
  const navigate = useNavigate();
  const { trigger: resetPassword } = useResetPassword();

  const {
    data: validateResponse,
    error: validateError,
    isLoading: isCheckingLink,
  } = useValidateResetLink(resetCode, time);

  useEffect(() => {
    if (!resetCode || !time) {
      setIsExpired(true);
      setError("Resetlinkparameters ontbreken.");
      return;
    }

    if (validateResponse) {
      if (validateResponse?.status === "success") {
        setMessage(
          "Uw resetlink is geldig. U kunt nu een nieuw wachtwoord instellen."
        );
      } else {
        setIsExpired(true);
        setError("Ongeldige of verlopen resetlink.");
      }
    }

    if (validateError) {
      setIsExpired(true);
      setError("De link is verlopen.");
    }
  }, [validateResponse, validateError, resetCode, time]);

  const handleSubmit = (values, { setSubmitting }) => {
    resetPassword({
      data: {
        resetcode: resetCode,
        time,
        password: values.password,
        password_confirmation: values.confirmPassword,
      },
    })
      .then((response) => {
        if (response.status == true) {
          setSuccessMessage("Wachtwoord succesvol bijgewerkt!");
          setMessage(null);
          setError(null);
          setTimeout(() => navigate("/login"), 1500);
        } else {
          setError("Wachtwoorden komen niet overeen");
          setSuccessMessage(null);
          setMessage(null);
        }
      })
      .catch(() => {
        setError("Serverfout tijdens het resetten van het wachtwoord.");
        setSuccessMessage(null);
        setMessage(null);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const renderPasswordField = ({ id, name, label, show, toggleShow, placeholder, autoComplete }) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="password-input">
        <Field
          id={id}
          name={name}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck="false"
        />
        <button
          className="eye-button"
          type="button"
          onClick={toggleShow}
          aria-pressed={show}
          aria-label={show ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
        >
          <svg className="eye-open" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <svg className="eye-closed" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.2A9.8 9.8 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.1 6.1C3.6 7.8 2 12 2 12s3.5 7 10 7c1.7 0 3.2-.4 4.5-1" />
          </svg>
        </button>
      </div>
      <ErrorMessage name={name} component="p" className="field-error" />
    </div>
  );

  const renderBackLink = () => (
    <a className="return-button" href="/login">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 5-7 7 7 7M5 12h14" /></svg>
      Terug naar inloggen
    </a>
  );

  const renderCardContent = () => {
    /* 1. Checking the link */
    if (isCheckingLink) {
      return (
        <Fragment>
          <div className="card-heading">
            <p className="eyebrow">CLOOSER</p>
            <h1 id="form-title" tabIndex={-1}>Even geduld...</h1>
            <p>Uw resetlink wordt gecontroleerd.</p>
          </div>
          <div className="form-notice" role="status">
            Resetlink controleren...
          </div>
        </Fragment>
      );
    }

    /* 2. Link expired / invalid */
    if (isExpired) {
      return (
        <Fragment>
          <div className="card-heading">
            <p className="eyebrow">CLOOSER</p>
            <h1 id="form-title" tabIndex={-1}>Resetlink verlopen</h1>
            <p>Uw resetlink is verlopen of ongeldig.</p>
          </div>

          <div className="form-notice notice-warning notice-with-icon" role="status">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <span>Vraag een nieuwe resetlink aan om uw wachtwoord opnieuw in te stellen.</span>
          </div>

          <button
            className="primary-button recovery-submit"
            type="button"
            onClick={() => navigate("/forgot-password")}
          >
            <span>Vraag een nieuwe resetlink aan</span>
          </button>

          {renderBackLink()}
        </Fragment>
      );
    }

    /* 3. Valid link: new password form */
    return (
      <Fragment>
        <div className="card-heading">
          <p className="eyebrow">CLOOSER</p>
          <h1 id="form-title" tabIndex={-1}>Nieuw wachtwoord</h1>
          <p>Stel een nieuw wachtwoord in voor uw Clooser-account.</p>
        </div>

        {message && (
          <div className="form-notice" role="status">{message}</div>
        )}
        {successMessage && (
          <div className="form-notice" role="status">{successMessage}</div>
        )}
        {error && (
          <div className="form-notice notice-error" role="status">{error}</div>
        )}

        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form noValidate>
              {renderPasswordField({
                id: "reset-password",
                name: "password",
                label: "Nieuw wachtwoord",
                show: showPassword,
                toggleShow: () => setShowPassword(!showPassword),
                placeholder: "Uw nieuwe Wachtwoord",
                autoComplete: "new-password",
              })}

              {renderPasswordField({
                id: "reset-password-confirm",
                name: "confirmPassword",
                label: "Bevestig wachtwoord",
                show: showConfirmPassword,
                toggleShow: () => setShowConfirmPassword(!showConfirmPassword),
                placeholder: "Herhaal uw nieuwe wachtwoord",
                autoComplete: "new-password",
              })}

              <button
                className="primary-button recovery-submit"
                type="submit"
                disabled={isSubmitting}
              >
                <span>{isSubmitting ? "Bezig..." : "Bevestigen"}</span>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" /></svg>
              </button>

              {renderBackLink()}
            </Form>
          )}
        </Formik>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <div className="forgot-password-page">
        <div className="backdrop" aria-hidden="true">
          <div className="circle circle-blue"></div>
          <div className="circle circle-orange"></div>
          <div className="circle circle-outline"></div>
        </div>

        <header className="page-header">
          <a className="quiet-link back-link" href="https://clooser.nl/">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 5-7 7 7 7M5 12h14" /></svg>
            Naar clooser.nl
          </a>
          <nav className="header-actions" aria-label="Ondersteuning">
            <a className="help-link status-link" href="https://www.clooserstatus.nl/" target="_blank" rel="noopener noreferrer">
              Systeemstatus
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M7 7h10v10" /></svg>
            </a>
            <a className="help-link" href="https://www.clooser.nl/vervangers/contact/" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 0 1 5 .5c0 2-2.5 2-2.5 4M12 17h.01" /></svg>
              Hulp nodig?
            </a>
          </nav>
        </header>

        <main className="login-form-page">
          <div className="login-wrap">
            <a className="brand" href="https://clooser.nl/" aria-label="Clooser.nl, naar de website">
              <img src={ClooserLogo} alt="Clooser.nl" width="2172" height="724" />
            </a>

            <section className="login-card" aria-labelledby="form-title">
              {renderCardContent()}

              <div className="card-footers">
                <span>Komt u er niet uit?</span>
                <a href="https://www.clooser.nl/vervangers/contact/" target="_blank" rel="noopener noreferrer">
                  Wij helpen u graag
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M7 7h10v10" /></svg>
                </a>
              </div>
            </section>
          </div>
        </main>
      </div>
    </Fragment>
  );
};

export default ChangedPassword;
